import {
  ReconciliationSupplierOption,
  ReconciliationSupplierRow,
  useGetReconciliationSummaryApi,
  useGetReconciliationSuppliersApi,
} from 'apis/reconciliation';
import { BaseTableContainer } from 'components/base-table';
import {
  BasePagination,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  PlusIcon,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useDisclosure,
} from 'design-system';
import { useDebounce } from 'hooks/use-debounce';
import { usePageParams } from 'hooks/use-page-params';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { AddSoaModal } from 'pages/reconciliation/components/add-soa-modal';
import { KpiCards } from 'pages/reconciliation/components/kpi-cards';
import { MonthSwitcher } from 'pages/reconciliation/components/month-switcher';
import { StatusTabs } from 'pages/reconciliation/components/status-tabs';
import { SupplierCard } from 'pages/reconciliation/components/supplier-card';
import { SupplierRow } from 'pages/reconciliation/components/supplier-row';
import { getMonthParam } from 'pages/reconciliation/utils/month';
import { useEffect, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

export const ReconciliationScreen = () => {
  const dispatch = useAppDispatch();
  const setPaginationParams = useSetPaginationParamsV1();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const month = getMonthParam(params);

  const { search } = params;
  const [searchInput, setSearchInput] = useState((search as string) || '');
  const debouncedSearch = useDebounce(searchInput, 400);

  const { data: summary, isLoading: isSummaryLoading } = useGetReconciliationSummaryApi(month);
  const { data, isLoading, isFetching } = useGetReconciliationSuppliersApi(month);
  const { data: rows, total, limit, statusCounts } = data ?? {};
  // The BE list response has no pageCount field (unlike older list endpoints), so derive it locally.
  const pagesCount = Math.ceil((total ?? 0) / (limit || 1));

  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
  const { isOpen: isAddSoaOpen, onOpen: onAddSoaOpen, onClose: onAddSoaClose } = useDisclosure();
  // One shared AddSoaModal instance serves both entry points — the header's blank "Add SOA"
  // and each expanded row's "Edit SOA" (which pre-selects that row's supplier). The initial
  // supplier is per-open state, set just before onAddSoaOpen.
  const [soaInitialSupplier, setSoaInitialSupplier] = useState<ReconciliationSupplierOption | null>(null);

  const openAddSoa = () => {
    setSoaInitialSupplier(null);
    onAddSoaOpen();
  };

  const openEditSoa = (row: ReconciliationSupplierRow) => {
    setSoaInitialSupplier({ value: row.supplierId, label: row.supplierName });
    onAddSoaOpen();
  };

  useEffect(() => {
    dispatch(changeTitle('Reconciliation'));
  }, []);

  // Keep the input's local echo in sync when the URL param changes from elsewhere
  // (e.g. ClearFilterButton, browser back/forward).
  useEffect(() => {
    setSearchInput((search as string) || '');
  }, [search]);

  useEffect(() => {
    const next = getPageParams();
    if ((next.search as string | undefined) === debouncedSearch) return;
    delete next.page;
    setPageParams({ ...next, search: debouncedSearch || undefined });
  }, [debouncedSearch]);

  const toggleRow = (supplierId: number) => setExpandedIds((prev) => ({ ...prev, [supplierId]: !prev[supplierId] }));

  // Below md the table (924px minimum) becomes a card list — one of the two is mounted,
  // both consuming the same rows/expansion state.
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Stack spacing='md'>
      <Flex justify='space-between' align='center' wrap='wrap' rowGap='sm'>
        <Stack spacing='0'>
          <Text fontSize='xl' fontWeight='bold' color='gray.800'>
            Reconciliation
          </Text>
          <Text fontSize='sm' color='gray.500'>
            Platform ordering GMV vs supplier statements of account
          </Text>
        </Stack>

        <HStack spacing='sm' flexWrap='wrap' rowGap='sm'>
          <MonthSwitcher />
          <Button colorScheme='red' leftIcon={<PlusIcon fill='white' />} onClick={openAddSoa}>
            Add SOA
          </Button>
        </HStack>
      </Flex>

      <KpiCards summary={summary} isLoading={isSummaryLoading} />

      <BaseTableContainer isLoading={isLoading}>
        <Flex
          px='md'
          pt='md'
          pb='md'
          gap='sm'
          align='center'
          justify='space-between'
          wrap='wrap'
          borderBottom='1px solid'
          borderColor='gray.200'>
          <StatusTabs statusCounts={statusCounts} />

          <InputGroup maxW='240px'>
            <InputLeftElement pointerEvents='none'>
              <FiSearch color='gray' />
            </InputLeftElement>
            <Input
              size='sm'
              placeholder='Search supplier…'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>
        </Flex>

        {isMobile ? (
          <Box flexGrow={1} overflowY='auto' minH='200px' p='sm'>
            {rows?.length ? (
              <Stack spacing='10px'>
                {rows.map((row) => (
                  <SupplierCard
                    key={row.supplierId}
                    row={row}
                    month={month}
                    isExpanded={!!expandedIds[row.supplierId]}
                    onToggle={() => toggleRow(row.supplierId)}
                    onEditSoa={() => openEditSoa(row)}
                  />
                ))}
              </Stack>
            ) : (
              <Text textAlign='center' color='gray.400' fontSize='sm' py='48px'>
                No suppliers match — clear the search or filter.
              </Text>
            )}
          </Box>
        ) : (
          <BaseTableContainer.TableWrapper>
            {/*
            tableLayout='fixed' + w='100%' + minW matching the column widths below
            (44+200+128+128+106+118+200=924) is load-bearing for the expanded row, not
            cosmetic. With the default table-layout:auto, a `width` on a <table> is only
            a *minimum* used-value — the browser is still free to grow the table to its
            content's max-content size, and per the flexbox spec a wrapping flex
            container's max-content contribution is computed as if it never wrapped (wrap
            only applies once a definite width is established). So the expanded row's
            two-panel Flex (see supplier-row.tsx) was inflating the WHOLE table to ~1362px
            — even though the collapsed rows only need 924px — and the wrap='wrap' escape
            hatch never got a chance to engage against a real width.
            table-layout:fixed changes this: column (and thus table) widths are resolved
            purely from the declared widths in the first row (the Thead below), ignoring
            what any cell's content (incl. the colSpan=7 expanded-row cell) would
            otherwise demand. That gives the expanded Flex a definite,
            already-established containing-block width to wrap against, so on narrow
            containers it wraps below using the SAME 924px canvas the collapsed rows
            already use, instead of demanding a wider one of its own.
            Supplier is the ONLY column without a declared width: under fixed layout the
            single auto column receives ALL leftover space, so on wide screens the
            chevron/numeric/collection columns keep their designed pixel widths and the
            supplier name alone absorbs the slack (mirroring the prototype's
            `minmax(200px, 1fr)` semantics); at the table's 924px minimum it resolves to
            exactly the designed 200px (924 − 44 − 128 − 128 − 106 − 118 − 200 = 200).
          */}
            <Table size='sm' w='100%' minW='924px' sx={{ tableLayout: 'fixed' }}>
              <Thead position='sticky' top={0} bg='white' zIndex={1}>
                <Tr>
                  <Th w='44px' />
                  <Th>Supplier</Th>
                  <Th isNumeric w='128px'>
                    Platform GMV (AED)
                  </Th>
                  <Th isNumeric w='128px'>
                    SOA GMV (AED)
                  </Th>
                  <Th isNumeric w='106px'>
                    Variance
                  </Th>
                  <Th isNumeric w='118px'>
                    Revenue (AED)
                  </Th>
                  <Th w='200px'>Collection</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows?.length ? (
                  rows.map((row) => (
                    <SupplierRow
                      key={row.supplierId}
                      row={row}
                      month={month}
                      isExpanded={!!expandedIds[row.supplierId]}
                      onToggle={() => toggleRow(row.supplierId)}
                      onEditSoa={() => openEditSoa(row)}
                    />
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={7} textAlign='center' color='gray.400' fontSize='sm' py='48px'>
                      No suppliers match — clear the search or filter.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </BaseTableContainer.TableWrapper>
        )}

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left>
            <Text fontSize='sm' color='gray.400'>
              {isFetching ? 'Refreshing…' : 'Click a row to expand collection & invoicing'}
            </Text>
          </BaseTableContainer.Footer.Left>
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pagesCount} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      <AddSoaModal isOpen={isAddSoaOpen} onClose={onAddSoaClose} month={month} initialSupplier={soaInitialSupplier} />
    </Stack>
  );
};
